import { useState } from "react";

import { toggleValue } from "common/utils/sets";

import SelectButton from "client/common/components/buttons/SelectButton";
import TextButton from "client/common/components/buttons/TextButton";

import { MODAL_COLLAPSED_OPTION_LIMIT } from "../constants/filterDefinitions";
import { SearchFilterOption } from "../types";
import { collapsedOptions } from "../utils";

import styles from "../styles/SearchFilter.module.less";

type SearchFilterProps<T extends string | number | boolean> = {
  options: SearchFilterOption<T>[];
  value: T[];
  onChange: (val: T[]) => void;
  /** Off unless the caller's options are the complete set, whose counts track the narrowing. */
  showCounts?: boolean;
  /**
   * Collapse long option lists behind a "Show all" toggle. Separate from `showCounts`, because
   * a surface can have the complete set and still pass no counts.
   */
  isCollapsible?: boolean;
};

const SearchFilter = <T extends string | number | boolean = number>({
  options,
  value,
  onChange,
  showCounts = false,
  isCollapsible = false,
}: SearchFilterProps<T>) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const enabledValues = new Set(value);

  const onToggle = (val: T) => onChange([...toggleValue(value, val)]);

  // A broad search returns dozens of options; uncollapsed, Category alone ran four phone screens.
  const visible =
    isExpanded || !isCollapsible
      ? options
      : collapsedOptions({
          options,
          selected: value,
          limit: MODAL_COLLAPSED_OPTION_LIMIT,
        });

  return (
    <>
      <div className={styles.options}>
        {visible.map((option) => (
          <SelectButton
            key={String(option.value)}
            align="center"
            isActive={enabledValues.has(option.value)}
            onClick={() => onToggle(option.value)}
            renderContent={(props) => (
              <SelectButton.DefinitionContent
                {...props}
                label={option.label}
                // Stringified so a zero count still renders; DefinitionContent hides a falsy value.
                description={showCounts ? String(option.count) : ""}
                status={null}
              />
            )}
          />
        ))}
      </div>
      {visible.length < options.length && (
        <TextButton className={styles.showAll} type="button" onClick={() => setIsExpanded(true)}>
          Show all {options.length}
        </TextButton>
      )}
      {isExpanded && (
        <TextButton className={styles.showAll} type="button" onClick={() => setIsExpanded(false)}>
          Show less
        </TextButton>
      )}
    </>
  );
};

export default SearchFilter;
